import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Smartphone, Bell, Volume2 } from 'lucide-react';

const STORAGE_KEY = 'alertOnboardingCompleted_v1';

/**
 * One-time onboarding modal shown on first visit after login.
 *
 * Three jobs:
 *   1. Prime the iOS WebAudio context (Apple requires a user gesture before
 *      audio will play — without this, the alert siren is silent until the
 *      user has tapped something on the page).
 *   2. Trigger the browser's "request permission to send notifications"
 *      flow so locked-phone push notifications work.
 *   3. Tell users how to add the app to their Home Screen — this is the
 *      only path to lock-screen push notifications on iOS 16.4+.
 *
 * Detects iOS vs Android vs Desktop and shows tailored instructions.
 */
export const AlertOnboardingModal = () => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0); // 0 = welcome, 1 = enable, 2 = install

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMobile = isIOS || isAndroid;
  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator?.standalone === true);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;
    // Desktop users don't need the iOS-audio / Home-Screen ceremony — they
    // already get the alert overlay + push notifications natively.
    if (!isMobile) {
      localStorage.setItem(STORAGE_KEY, 'true');
      return;
    }
    // Brief delay so the dashboard renders first then the modal pops up.
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setShow(false);
  };

  // Step "enable" — does the work that requires a user gesture.
  const enableAlerts = async () => {
    // 1. Prime audio context (iOS requirement).
    try {
       
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        // Tiny inaudible blip to unlock the context.
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.001;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.05);
      }
    } catch (e) { console.debug('[onboarding] audio prime failed:', e?.message); }

    // 2. Buzz once on Android to prime the vibrate permission.
    if ('vibrate' in navigator) {
      try { navigator.vibrate([100, 50, 100]); } catch (e) { console.debug('[onboarding] vibrate prime failed:', e?.message); }
    }

    // 3. Request notification permission so push notifications work.
    if ('Notification' in window && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch (e) { console.debug('[onboarding] notif perm failed:', e?.message); }
    }

    setStep(2);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="alert-onboarding-modal"
    >
      <div className="bg-panel border-2 border-primary/40 rounded-sm max-w-md w-full overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-primary/10">
          <span className="font-chivo font-bold text-sm uppercase tracking-wider text-white">
            🚨 Set up alerts on this device
          </span>
          <button
            onClick={finish}
            className="h-7 w-7 flex items-center justify-center rounded-sm hover:bg-white/10 text-muted-foreground hover:text-white"
            data-testid="onboarding-skip-btn"
            aria-label="Skip"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 0 && (
          <div className="p-5 space-y-4">
            <p className="font-inter text-sm text-white leading-relaxed">
              In a real incident, every second matters. Take 20 seconds now to make sure
              your phone will actually <strong>alert you</strong> when a teammate hits the panic button.
            </p>

            <div className="space-y-2 font-mono text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Bell className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span>Permission to send <strong>push notifications</strong> (works even when phone is locked)</span>
              </div>
              <div className="flex items-start gap-2">
                <Volume2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span>Permission to play <strong>alert sound</strong> {isIOS && '(iPhone only — Apple requires audio to wake the phone)'}</span>
              </div>
              <div className="flex items-start gap-2">
                <Smartphone className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span>Add the app to your <strong>Home Screen</strong> so alerts reach you outside the browser</span>
              </div>
            </div>

            <Button
              onClick={() => setStep(1)}
              className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
              data-testid="onboarding-start-btn"
            >
              Continue
            </Button>
            <button
              onClick={finish}
              className="w-full font-mono text-[10px] uppercase text-muted-foreground hover:text-white"
              data-testid="onboarding-skip-link"
            >
              I&apos;ll set this up later
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="p-5 space-y-4">
            <p className="font-inter text-sm text-white leading-relaxed">
              Tap the button below. Your browser will ask for:
              <span className="block mt-2 ml-4 font-mono text-xs text-muted-foreground">
                • Notifications → <strong className="text-white">Allow</strong><br />
                • Sound / Audio playback → <strong className="text-white">Allow</strong>
              </span>
            </p>
            <Button
              onClick={enableAlerts}
              className="w-full h-12 bg-primary hover:bg-primary/90 font-black uppercase tracking-wider text-base rounded-sm"
              data-testid="onboarding-enable-btn"
            >
              ⚡ Enable alerts on this device
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="p-5 space-y-4">
            <p className="font-inter text-sm text-green-400">
              ✓ Alerts enabled on this device.
            </p>

            {isMobile && !isStandalone && (
              <>
                <div className="border-t border-white/10 pt-3 space-y-2">
                  <p className="font-chivo font-bold text-xs uppercase tracking-wider text-white">
                    One more step — add to Home Screen
                  </p>
                  <p className="font-mono text-[11px] text-muted-foreground">
                    Without this, the app can only alert you while you have the browser open. Once installed, lock-screen alerts will work even with the app closed.
                  </p>

                  {isIOS && (
                    <ol className="font-inter text-xs text-white space-y-1.5 pl-1">
                      <li><strong>1.</strong> Tap the <strong>Share</strong> button (square with up arrow) at the bottom of Safari</li>
                      <li><strong>2.</strong> Scroll and tap <strong>&quot;Add to Home Screen&quot;</strong></li>
                      <li><strong>3.</strong> Tap <strong>Add</strong> in the top right</li>
                      <li><strong>4.</strong> Open the new icon from your Home Screen — that&apos;s the version that supports lock-screen push</li>
                    </ol>
                  )}

                  {isAndroid && (
                    <ol className="font-inter text-xs text-white space-y-1.5 pl-1">
                      <li><strong>1.</strong> Tap the <strong>⋮</strong> menu (top right of Chrome)</li>
                      <li><strong>2.</strong> Tap <strong>&quot;Install app&quot;</strong> or <strong>&quot;Add to Home screen&quot;</strong></li>
                      <li><strong>3.</strong> Tap <strong>Install</strong></li>
                    </ol>
                  )}
                </div>
              </>
            )}

            {isIOS && (
              <div className="border-t border-white/10 pt-3">
                <p className="font-mono text-[10px] text-yellow-400 leading-relaxed">
                  <strong>iPhone note:</strong> Apple does not support physical vibration in web apps. You&apos;ll hear a loud siren instead — that&apos;s by design. Real vibration will be available when the native iOS app ships.
                </p>
              </div>
            )}

            <Button
              onClick={finish}
              className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
              data-testid="onboarding-done-btn"
            >
              Done — ready for alerts
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertOnboardingModal;
